import { HubInfo } from '../../meta-os-app-container.interface';
import { fullPermissionApps, appUrlResource, AllowedRuntimeAppContexts } from '../constants';
import { AppContext, ContextBridge, IAppDefinition, IWebContentContext } from '../hosted-web-content-sdk.interface';
import { HubNames } from '../messages.interface';
import { CaseInsensitiveSet } from './CaseInsensitiveSet';

export const getValidDomainFromUrl = (url: string): string => url && new URL(url).hostname;

/**
 * to validate origin against valid domains for a hosted web content
 * @param origin: web content origin
 */
export const isValidUrlForApp = (
  url: string,
  appDefinition?: IAppDefinition,
  appContext?: IWebContentContext
): boolean =>
  url !== undefined &&
  appDefinition !== undefined &&
  isUrlInDomainList(hydrateUrl(url, appContext), getValidDomainsForApp(appDefinition, appContext));

export const getValidDomainsForApp = (appDefinition: IAppDefinition, appContext?: IWebContentContext): string[] => {
  const validDomains = new CaseInsensitiveSet();

  if (appDefinition.validDomains) {
    appDefinition.validDomains.forEach(validDomains.add);
  }

  if (appDefinition.galleryTabs) {
    appDefinition.galleryTabs.forEach((gt) =>
      validDomains.add(getValidDomainFromUrl(hydrateUrl(gt.configurationUrl, appContext)))
    );
  }
  if (appDefinition.staticTabs) {
    appDefinition.staticTabs.forEach((st) => {
      if (st.contentUrl) {
        validDomains.add(getValidDomainFromUrl(hydrateUrl(st.contentUrl, appContext)));
      }
    });
  }

  return Array.from(validDomains.values());
};

// TODO: Update all full trust apps in MT and remove list of overrides in TMP and TSW
// https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/1049320
export const hasFullPermission = (appId: string) => appId && fullPermissionApps.indexOf(appId.toLowerCase()) !== -1;

export const hasFullPermissionOrIsLobApp = (appId: string, tenantId?: string) => hasFullPermission(appId) || !!tenantId;

export const getValidatedResources = (
  origin: string,
  resources: string[],
  hubInfo: HubInfo,
  appDefinition?: IAppDefinition,
  appContext?: IWebContentContext
): Promise<string[]> => {
  if (
    hubInfo.name === HubNames.teams &&
    (!appDefinition /* TODO make app definition required for shareTo Teams, Digital Signage */ ||
      appDefinition.isFullTrustApp ||
      hasFullPermission(appDefinition.appId))
  ) {
    return Promise.resolve(resources);
  } else if (appDefinition?.webApplicationInfo?.resource) {
    const resourceForApp = getResourceForApp(appDefinition, appContext);
    if (resourceForApp && isValidResource(resourceForApp, origin)) {
      return Promise.resolve([resourceForApp]);
    } else {
      return Promise.reject('App resource defined in manifest and iframe origin do not match');
    }
  } else {
    return Promise.reject('App webApplicationInfo or resource not defined in manifest');
  }
};

/**
 * Check for variable placeholder like {teamSiteDomain} and fills in values from app context
 * https://{teamSiteDomain}/test changes to  https://azure.websites.com/test
 * @param url input url
 * @param appContext web Context of the tab
 */
export const hydrateUrl = (url: string, appContext?: IWebContentContext): string => {
  return formatUrl(url, appContext, [], AllowedRuntimeAppContexts);
};

export const getResourceForApp = (appDefinition: IAppDefinition, appContext?: IWebContentContext): string | undefined =>
  appDefinition.webApplicationInfo?.resource && hydrateUrl(appDefinition.webApplicationInfo?.resource, appContext);

/**
 * Valid resources will contain the domain in the app url https:// or in the format of api://{domain}/
 * @param resource The resource being requested by the app
 * @param origin The calling app url
 */
export const isValidResource = (resource: string, origin: string): boolean => {
  if (resource && origin) {
    if (resource === origin) {
      return true;
    } else {
      const domain = getValidDomainFromUrl(origin);
      const resourceTestExpression = appUrlResource.validResourcePattern.replace(
        appUrlResource.resourceDomainParameter,
        domain
      );

      return new RegExp(resourceTestExpression, 'i').test(resource);
    }
  } else {
    return false;
  }
};

/**
 * Validate whether the given URL is a part of the given list of domains
 * @param {string} url - URL to be validated
 * @param {string[]} domains - List of valid domains
 * @returns {boolean} - True if the url belongs to the valid domains, else False
 */
export const isUrlInDomainList = (url: string, domains: ReadonlyArray<string>): boolean => {
  // Normalize the tab URL removing the default port if present
  const normalizedUrl = removeDefaultPort(url);
  // Keep track of all the domains already checked
  const checkedDomains = new CaseInsensitiveSet();

  // Check the URL against each domain in the list
  for (const domain of domains) {
    if (!checkedDomains.add(domain)) {
      continue;
    }

    let formattedDomain = '';
    const domainParts = domain.split('.');

    for (let j = 0; j < domainParts.length; j++) {
      formattedDomain += (j > 0 ? '[.]' : '') + domainParts[j].replace('*', '[^/^.]+');
    }

    // Check both the original URL and the normalized one against the pattern since
    // the developer might have specified the default port in their valid domains
    const regExPattern = new RegExp(`^https://${formattedDomain}((/|\\?).*)?$`, 'i');

    if (url.match(regExPattern) || normalizedUrl.match(regExPattern)) {
      return true;
    }
  }
  return false;
};

export const removeDefaultPort = (url: string) => {
  const parsedUrl = new URL(url);
  return parsedUrl.href;
};

/**
 * Formats an URL in order to make it ready for navigation. The process
 * involves replacing all of the placeholders in the URL with their values in the
 * app context (e.g. {app.sessionId}, {user.tenant.id}, {userPrincipalName}, {tid}, {groupId}, etc.).
 */
export const formatUrl = (
  url: string,
  propertyValue: any,
  propertyPath: string[],
  pathListAllowList?: string[]
): string => {
  if (propertyValue) {
    if (typeof propertyValue === 'object') {
      for (const field in propertyValue) {
        url = formatUrl(url, propertyValue[field], propertyPath.concat(field), pathListAllowList);
      }
    } else {
      const path = propertyPath.join('.');

      if (pathListAllowList === undefined || pathListAllowList.includes(path)) {
        const regExPattern = new RegExp(`{${path}}`, 'g');
        url = url.replace(regExPattern, encodeURIComponent(propertyValue as string | number | boolean));
      }
    }
  }
  return url;
};

/**
 * Transforms the AppContext object to the client SDK readable format.
 * It is also the old context object format
 */
export function transformContext(appSDKContext: AppContext): ContextBridge {
  const context: ContextBridge = {
    // app
    locale: appSDKContext.app.locale,
    appSessionId: appSDKContext.app.sessionId,
    theme: appSDKContext.app.theme,
    appIconPosition: appSDKContext.app.iconPositionVertical,
    osLocaleInfo: appSDKContext.app.osLocaleInfo,
    parentMessageId: appSDKContext.app.parentMessageId,
    userClickTime: appSDKContext.app.userClickTime,
    userFileOpenPreference: appSDKContext.app.userFileOpenPreference,

    // app.host
    hostName: appSDKContext.app.host.name,
    hostClientType: appSDKContext.app.host.clientType,
    sessionId: appSDKContext.app.host.sessionId,
    ringId: appSDKContext.app.host.ringId,

    // page
    entityId: appSDKContext.page.id,
    frameContext: appSDKContext.page.frameContext,
    subEntityId: appSDKContext.page.subPageId,
    isFullScreen: appSDKContext.page.isFullScreen,
    isMultiWindow: appSDKContext.page.isMultiWindow,
    sourceOrigin: appSDKContext.page.sourceOrigin,

    // user
    userObjectId: appSDKContext.user !== undefined ? appSDKContext.user.id : undefined,
    isCallingAllowed: appSDKContext.user !== undefined ? appSDKContext.user.isCallingAllowed : undefined,
    isPSTNCallingAllowed: appSDKContext.user !== undefined ? appSDKContext.user.isPSTNCallingAllowed : undefined,
    userLicenseType: appSDKContext.user !== undefined ? appSDKContext.user.licenseType : undefined,
    loginHint: appSDKContext.user !== undefined ? appSDKContext.user.loginHint : undefined,
    userPrincipalName: appSDKContext.user !== undefined ? appSDKContext.user.userPrincipalName : undefined,
    userDisplayName: appSDKContext.user !== undefined ? appSDKContext.user.displayName : undefined,

    // user.tenant
    tid:
      appSDKContext.user !== undefined
        ? appSDKContext.user.tenant !== undefined
          ? appSDKContext.user.tenant.id
          : undefined
        : undefined,
    tenantSKU:
      appSDKContext.user !== undefined
        ? appSDKContext.user.tenant !== undefined
          ? appSDKContext.user.tenant.sku
          : undefined
        : undefined,

    // channel
    channelId: appSDKContext.channel !== undefined ? appSDKContext.channel.id : undefined,
    channelName: appSDKContext.channel !== undefined ? appSDKContext.channel.displayName : undefined,
    channelRelativeUrl: appSDKContext.channel !== undefined ? appSDKContext.channel.relativeUrl : undefined,
    channelType: appSDKContext.channel !== undefined ? appSDKContext.channel.membershipType : undefined,
    defaultOneNoteSectionId:
      appSDKContext.channel !== undefined ? appSDKContext.channel.defaultOneNoteSectionId : undefined,
    hostTeamGroupId: appSDKContext.channel !== undefined ? appSDKContext.channel.ownerGroupId : undefined,
    hostTeamTenantId: appSDKContext.channel !== undefined ? appSDKContext.channel.ownerTenantId : undefined,

    // chat
    chatId: appSDKContext.chat !== undefined ? appSDKContext.chat.id : undefined,

    // meeting
    meetingId: appSDKContext.meeting !== undefined ? appSDKContext.meeting.id : undefined,

    // sharepoint
    sharepoint: appSDKContext.sharepoint,

    // team
    teamId: appSDKContext.team !== undefined ? appSDKContext.team.internalId : undefined,
    teamName: appSDKContext.team !== undefined ? appSDKContext.team.displayName : undefined,
    teamType: appSDKContext.team !== undefined ? appSDKContext.team.type : undefined,
    groupId: appSDKContext.team !== undefined ? appSDKContext.team.groupId : undefined,
    teamTemplateId: appSDKContext.team !== undefined ? appSDKContext.team.templateId : undefined,
    isTeamArchived: appSDKContext.team !== undefined ? appSDKContext.team.isArchived : undefined,
    userTeamRole: appSDKContext.team !== undefined ? appSDKContext.team.userRole : undefined,

    // sharepointSite
    teamSiteUrl: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.url : undefined,
    teamSiteDomain: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.domain : undefined,
    teamSitePath: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.path : undefined,
  };

  return context;
}
